import { useDocumentTitleEntity } from '@components/DocumentTitle'
import { HeaderLangSelector } from '@components/localizable/HeaderLangSelector'
import { Localizable } from '@components/localizable/Localizable'
import { Venue } from '@components/venue/Venue'
import { resetFeaturedVenuesQueryAndIds } from '@store/FeaturedVenuesReducer'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { resetMenuQueryAndIds } from '@store/MenuReducer'
import { resetSeatingAreaQueryAndIds } from '@store/SeatingAreaReducer'
import {
  getVenueWithSlug,
  selectVenueBySlug,
  setSelectedVenue,
  setSelectedVenueWithSlug,
} from '@store/VenueReducer'
import { resetVenuesCollectionQueryAndIds } from '@store/VenuesCollectionReducer'
import { resetVenueSeatingAreaQueryAndIds } from '@store/VenueSeatingAreaReducer'
import { resetWhatsappBotSettingState } from '@store/WhatsappBotSettingReducer'
import { extractSectionFromPath } from '@supplement-bacon/react-toolkit/utils'
import { initialQuery } from '@supplement-bacon/sb-table-filters'
import {
  Flex,
  Layout,
  Space,
  Spin,
  Tabs,
  TabsProps,
  theme,
  Typography,
} from 'antd'
import { Content } from 'antd/es/layout/layout'
import { useEffect, useState } from 'react'
import {
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useParams,
} from 'react-router-dom'
import { LayoutFooter } from '../components/LayoutFooter'
import MainHeader from '../components/MainHeader'

const { Title, Text } = Typography

export const VenueDetailLayout: React.FC = () => {
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const { token } = theme.useToken()
  const { venueSlug } = useParams()
  const { pathname } = useLocation()
  const [selectedKey, setSelectedKey] = useState<string>('overview')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const venue = useAppSelector(selectVenueBySlug(venueSlug ?? ''))
  useDocumentTitleEntity(!venue || isLoading ? undefined : venue.name)

  const items: TabsProps['items'] = [
    {
      key: 'overview',
      label: 'Overview',
    },
    {
      key: 'generals',
      label: 'Informations',
    },
    {
      key: 'customize',
      label: 'Customize',
    },
    {
      key: 'booking',
      label: 'Booking',
    },
    {
      key: 'nested-venues',
      label: 'Nested Venues',
    },
    {
      key: 'legals',
      label: 'Legals',
    },
    {
      key: 'settings',
      label: 'Settings',
    },
  ]

  const fetchVenue = () => {
    if (!venueSlug) {
      navigate('/list')
      return
    }

    setIsLoading(true)
    dispatch(getVenueWithSlug(venueSlug))
      .then(() => {
        dispatch(setSelectedVenueWithSlug(venueSlug))
      })
      .catch(() => {
        navigate('/')
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  useEffect(() => {
    setSelectedKey(extractSectionFromPath(pathname, 3))
  }, [pathname])

  useEffect(() => {
    fetchVenue()

    dispatch(resetFeaturedVenuesQueryAndIds(initialQuery))
    dispatch(resetMenuQueryAndIds(initialQuery))
    dispatch(resetVenuesCollectionQueryAndIds(initialQuery))
    dispatch(resetWhatsappBotSettingState())
    dispatch(resetSeatingAreaQueryAndIds(initialQuery))
    dispatch(resetVenueSeatingAreaQueryAndIds(initialQuery))
  }, [dispatch, venueSlug])

  useEffect(() => {
    if (venue) {
      dispatch(setSelectedVenue(venue.id))
    }
  }, [venue])

  return !venue || isLoading ? (
    <Layout className="min-h-100vh">
      <MainHeader />
      <Spin
        style={{
          top: '50%',
          left: '50%',
          position: 'absolute',
          transform: 'translate(-50%,-50%)',
        }}
      />
    </Layout>
  ) : (
    <Layout>
      <MainHeader />
      <Layout style={{ minHeight: '100vh', marginTop: 80 }}>
        <Content>
          <Flex vertical gap={10} className="container-row mt-0b">
            <Localizable headerSelector>
              <Space
                orientation="vertical"
                className="w-full"
                style={{
                  position: 'sticky',
                  background: token.colorBgLayout,
                  zIndex: 200,
                  top: 64,
                  paddingTop: 20,
                }}
              >
                <Space className="align-items-center w-full space-between">
                  <Space>
                    <Venue.AvatarModal
                      model={venue}
                      onSuccess={fetchVenue}
                      trigger={
                        <Venue.Avatar
                          model={venue}
                          type="condensed"
                          size={45}
                        />
                      }
                    />
                    <Title className="m-0" level={3}>
                      {venue.name}
                    </Title>
                  </Space>
                  <Space>
                    <Space orientation="vertical" size={0}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Visibility
                      </Text>
                      <Link to={`/venues/${venue.slug}/generals`}>
                        <Venue.VisibilityOverview model={venue} />
                      </Link>
                    </Space>
                    <Space orientation="vertical" size={0}>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        Booking
                      </Text>
                      <Venue.BookingOverview model={venue} linkToDetail />
                    </Space>
                  </Space>
                </Space>
                <Tabs
                  activeKey={selectedKey}
                  items={items}
                  tabBarExtraContent={<HeaderLangSelector />}
                  onChange={(key) => {
                    setSelectedKey(key)
                    navigate(key)
                  }}
                />
              </Space>
              <Flex vertical style={{ marginTop: 20 }}>
                <Outlet />
              </Flex>
            </Localizable>
          </Flex>
        </Content>
        <LayoutFooter />
      </Layout>
    </Layout>
  )
}
